import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronIcon, EffortIcon } from './icons';
import { EFFORT_OPTIONS, EFFORT_UNSUPPORTED_HINT, normalizeEffort } from '../utils/effortPreference.js';
import './EffortSelector.css';

const levelOf = (id) => Math.max(0, EFFORT_OPTIONS.findIndex((o) => o.id === id));

/**
 * Profondeur de réflexion (LLM-10) : Automatique, Rapide, Standard, Approfondi.
 * Bouton du pied du compositeur qui ouvre une petite boîte de dialogue (aria-haspopup
 * « dialog », role="dialog" titré) contenant un groupe de boutons radio :
 * flèches = changer de niveau, Entrée = valider, Échap = annuler (niveau d'origine).
 * `supported` false (modèle sans effort) : bouton inactif, avec l'indication.
 */
export function EffortSelector({ value, onChange, supported = true }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  // Niveau à l'ouverture du menu : rétabli par Échap
  const initialRef = useRef(value);
  const baseId = useId();
  const menuId = `${baseId}-menu`;
  const unsupportedId = `${baseId}-unsupported`;

  const current = EFFORT_OPTIONS.find((o) => o.id === normalizeEffort(value)) ?? EFFORT_OPTIONS[0];

  // Modèle sans effort choisi pendant que le menu est ouvert : fermeture
  useEffect(() => {
    if (!supported) setOpen(false);
  }, [supported]);

  // Ouverture : le niveau coché reçoit le focus (un seul arrêt de tabulation dans le groupe)
  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => optionRefs.current[levelOf(current.id)]?.focus());
    return () => cancelAnimationFrame(frame);
    // Seulement à l'ouverture : les flèches déplacent elles-mêmes le focus
  }, [open]);

  // Clic à l'extérieur : fermeture (le niveau choisi est conservé)
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openMenu = () => {
    if (!supported) return;
    initialRef.current = current.id;
    setOpen(true);
  };

  const close = ({ restore = false, focusTrigger = true } = {}) => {
    if (restore && initialRef.current !== current.id) onChange(initialRef.current);
    setOpen(false);
    if (focusTrigger) requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const select = (id) => {
    if (id !== current.id) onChange(id);
    close();
  };

  const onTriggerKeyDown = (event) => {
    if (!supported) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      openMenu();
    }
  };

  const onGroupKeyDown = (event) => {
    const index = levelOf(current.id);
    const last = EFFORT_OPTIONS.length - 1;
    let next = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close({ restore: true });
      return;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      close();
      return;
    } else if (event.key === 'Tab') {
      // Le focus quitte le menu : fermé, niveau conservé
      close({ focusTrigger: false });
      return;
    }
    if (next === null) return;
    event.preventDefault();
    onChange(EFFORT_OPTIONS[next].id);
    optionRefs.current[next]?.focus();
  };

  if (!supported) {
    return (
      <div className="effort-selector" ref={rootRef}>
        <button
          ref={triggerRef}
          type="button"
          className="effort-selector__trigger is-unsupported"
          aria-disabled="true"
          aria-describedby={unsupportedId}
          title={EFFORT_UNSUPPORTED_HINT}
        >
          <EffortIcon size={14} level={2} className="effort-selector__icon" />
          <span className="effort-selector__current">Profondeur fixe</span>
        </button>
        <span id={unsupportedId} className="sr-only">{EFFORT_UNSUPPORTED_HINT}</span>
      </div>
    );
  }

  return (
    <div className="effort-selector" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="effort-selector__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Profondeur de réflexion : ${current.label}`}
        title={`Profondeur de réflexion : ${current.label} — ${current.hint}`}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <EffortIcon size={14} level={levelOf(current.id)} className="effort-selector__icon" />
        <span className="effort-selector__current" aria-hidden="true">
          <span className="effort-selector__long">{current.label}</span>
          <span className="effort-selector__short">{current.short}</span>
        </span>
        <ChevronIcon size={14} className={`effort-selector__chevron ${open ? 'is-open' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={menuId}
            className="effort-selector__menu"
            role="dialog"
            aria-labelledby={`${menuId}-title`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
          >
            <p className="effort-selector__heading" id={`${menuId}-title`}>Profondeur de réflexion</p>
            <div role="radiogroup" aria-labelledby={`${menuId}-title`} onKeyDown={onGroupKeyDown}>
              {EFFORT_OPTIONS.map((option, i) => {
                const checked = option.id === current.id;
                const hintId = `${menuId}-${option.id}-hint`;
                return (
                  <button
                    key={option.id}
                    ref={(el) => { optionRefs.current[i] = el; }}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    aria-describedby={hintId}
                    tabIndex={checked ? 0 : -1}
                    className={`effort-selector__option ${checked ? 'is-selected' : ''}`}
                    onClick={() => select(option.id)}
                  >
                    <EffortIcon size={16} level={i} className="effort-selector__option-icon" />
                    <span className="effort-selector__option-text">
                      <span className="effort-selector__label">{option.label}</span>
                      <span id={hintId} className="effort-selector__hint">{option.hint}</span>
                    </span>
                    {checked && <CheckIcon size={16} className="effort-selector__check" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
